// Stale-bundle notice bus: a module that learns this tab's bundle is behind the deployed one
// raises a notice; <StaleBundleBanner/> (mounted once in App.jsx) renders the guided reload.
// Pattern of lib/ui/toast.js, with two differences a toast does not need:
//
// - Idempotent per page load: the first `noteStaleBundle(reason)` notifies, later calls with
//   the same reason are no-ops — the per-row wire gate (lib/apiClient.js) fires on EVERY list
//   load of a stale tab, and the user is told once, not on every poll.
// - Loop guard shared with lib/chunkReload.js: a notice is silent when a reload was stamped
//   within its cooldown. A reload that did not stop the gate dropping rows means the rows are
//   bad STORED rows, not a stale bundle — reloading again changes nothing, and the
//   `forecasts.wire_shape_failed` console.error already names them.
//
// - A notice raised before anyone subscribes (a list load that drops rows while App.jsx still
//   shows a pre-auth shell, before <StaleBundleBanner/> mounts) is held for the first subscriber
//   and delivered on subscribe, then forgotten — so it is still shown exactly once.
//
// `noteStaleBundle` never throws: a throwing or blocked sessionStorage is handled in
// chunkReload.js, and a throwing listener is caught per listener (logged as
// `staleBundle.listener_failed`, the next listener still notified), so the gate's "never throws"
// contract holds across the hook.
import { readSessionStorage, shouldOfferStaleReload } from "./chunkReload.js";

const listeners = new Set();
const noted = new Set();
const undelivered = new Map();

function deliver(fn, notice) {
  try {
    fn(notice);
  } catch (err) {
    console.error("staleBundle.listener_failed", err);
  }
}

/**
 * Raise the stale-bundle notice for `reason` (`"wire_shape"`: the gate dropped rows), once per
 * page load and only when no reload was just attempted.
 * @param {string} reason
 * @returns {boolean} whether the notice was raised (delivered now, or held for the first subscriber)
 */
export function noteStaleBundle(reason) {
  if (noted.has(reason)) return false;
  if (!shouldOfferStaleReload(readSessionStorage(), Date.now())) return false;
  noted.add(reason);
  const notice = { reason };
  if (listeners.size === 0) {
    undelivered.set(reason, notice);
    return true;
  }
  listeners.forEach((fn) => deliver(fn, notice));
  return true;
}

/**
 * Subscribe to stale-bundle notices; returns the unsubscribe function. A notice raised while
 * nobody was subscribed is delivered to this subscriber now, once.
 */
export function onStaleBundle(fn) {
  listeners.add(fn);
  if (undelivered.size > 0) {
    const held = [...undelivered.values()];
    undelivered.clear();
    held.forEach((notice) => deliver(fn, notice));
  }
  return () => listeners.delete(fn);
}

/** Forget every notice raised so far, delivered or held (tests). */
export function resetStaleBundle() {
  noted.clear();
  undelivered.clear();
}
